import { Link } from "react-router-dom";
import { ArrowRight, HomeIcon, Handshake, Building2, Compass } from "lucide-react";
import { IMG_HERO } from "@/lib/constants";

const pillars = [
  {
    icon: HomeIcon,
    title: "Housing Stability",
    body: "We help families navigate hardship and stay in their homes through real, practical solutions tailored to local realities.",
  },
  {
    icon: Handshake,
    title: "Dignified Exit",
    body: "When staying isn't possible, we create respectful, transparent pathways forward that protect homeowners' equity and peace of mind.",
  },
  {
    icon: Building2,
    title: "Community Investment",
    body: "We invest in properties and neighborhoods with purpose — strengthening housing inventory and the communities we love.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative isolate overflow-hidden"
        data-testid="home-hero"
      >
        <div className="absolute inset-0">
          <img
            src={IMG_HERO}
            alt="Hawaii coastline at sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-forest/85 via-brand-forest/70 to-brand-ink/80" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-32 md:py-44 lg:py-56">
          <div className="max-w-3xl animate-fade-up">
            <p className="eyebrow text-brand-gold" data-testid="hero-eyebrow">
              Hawaii Housing Foundation
            </p>
            <h1
              className="mt-6 font-heading text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
              data-testid="hero-title"
            >
              To keep <em className="text-brand-gold not-italic font-medium">Hawaii</em> home.
            </h1>
            <p className="mt-8 text-white/85 text-lg md:text-xl max-w-2xl leading-relaxed font-light">
              Solutions for today. Stability for tomorrow. We provide housing solutions that
              strengthen our communities — supporting homeowners, creating opportunities, and
              investing with purpose.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-get-help-btn"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-gold text-brand-ink text-[12px] uppercase tracking-[0.24em] font-semibold rounded-sm hover:bg-white transition-all duration-300"
              >
                Get Help
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/about"
                data-testid="hero-learn-more-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white text-[12px] uppercase tracking-[0.24em] font-semibold rounded-sm hover:bg-white/10 transition-all duration-300"
              >
                Our Mission
              </Link>
            </div>
          </div>
        </div>

        {/* decorative bottom wave */}
        <svg
          className="absolute bottom-0 left-0 w-full text-brand-ivory"
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M0,40 C320,90 720,0 1080,40 C1260,60 1380,55 1440,40 L1440,80 L0,80 Z"
          />
        </svg>
      </section>

      {/* MISSION STATEMENT */}
      <section className="relative bg-brand-ivory py-24 md:py-36 overflow-hidden" data-testid="home-mission">
        <div className="absolute inset-0 bg-topo opacity-100 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 lg:px-12">
          <p className="eyebrow text-brand-gold">Our Mission</p>
          <h2 className="mt-6 font-heading text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-brand-ink max-w-4xl" data-testid="mission-title">
            Every local family deserves housing stability, practical solutions, and a path forward
            during difficult times.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 gap-10 md:gap-16">
            <p className="text-brand-ink/75 leading-relaxed text-base md:text-lg font-light">
              Hawaii Housing Foundation was founded on a simple belief: every local family deserves
              access to housing stability and a path forward. Life happens — job loss, illness,
              rising costs — and too often people feel they have nowhere to turn.
            </p>
            <p className="text-brand-ink/75 leading-relaxed text-base md:text-lg font-light">
              Through strategic real estate investment, advocacy, education, and community
              partnerships, we create opportunities that benefit homeowners and neighborhoods alike.
              Unlike traditional investment groups focused solely on profit, we balance responsible
              investment with lasting community impact.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative bg-white py-24 md:py-32 border-y border-brand-forest/10" data-testid="home-pillars">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="flex items-end justify-between flex-wrap gap-8 mb-16">
            <div className="max-w-2xl">
              <p className="eyebrow text-brand-gold">What we do</p>
              <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl text-brand-ink leading-tight">
                Real solutions for real challenges.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="pillars-explore-link"
              className="group inline-flex items-center gap-2 text-brand-forest text-[12px] uppercase tracking-[0.24em] font-semibold link-underline"
            >
              Explore solutions
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <article
                  key={p.title}
                  data-testid={`pillar-card-${i}`}
                  className="group relative bg-brand-ivory border border-brand-forest/10 p-10 rounded-sm hover:bg-brand-forest hover:text-white transition-all duration-500"
                >
                  <Icon size={32} className="text-brand-gold transition-colors" />
                  <h3 className="mt-8 font-heading text-2xl text-brand-ink group-hover:text-white transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-brand-ink/70 group-hover:text-white/80 text-sm leading-relaxed font-light transition-colors">
                    {p.body}
                  </p>
                  <div className="mt-8 h-px w-12 bg-brand-gold" />
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUOTE / VISION */}
      <section className="relative bg-brand-cream py-24 md:py-32" data-testid="home-vision">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 text-center">
          <Compass size={36} className="mx-auto text-brand-gold" />
          <blockquote className="mt-10 font-heading text-2xl md:text-3xl lg:text-4xl text-brand-ink leading-[1.3] italic max-w-3xl mx-auto">
            "We believe housing should serve people first — and that long-term success is achieved
            by creating positive outcomes for homeowners, neighborhoods, and future generations."
          </blockquote>
          <p className="eyebrow text-brand-gold mt-10">— Hawaii Housing Foundation</p>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-brand-forest text-white py-24 md:py-32 overflow-hidden" data-testid="home-cta">
        <div className="absolute inset-0 bg-topo opacity-30" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 lg:px-12 text-center">
          <p className="eyebrow text-brand-gold">Aloha & Mahalo</p>
          <h2 className="mt-6 font-heading text-3xl md:text-4xl lg:text-5xl leading-tight" data-testid="home-cta-title">
            Facing a housing challenge? <br className="hidden md:block" />
            <span className="text-brand-gold">Let's find a path forward — together.</span>
          </h2>
          <p className="mt-8 text-white/80 max-w-2xl mx-auto text-lg font-light">
            Whether you're a homeowner navigating hardship, a community partner, or someone who
            wants to help — we want to hear from you.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="home-cta-contact-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-gold text-brand-ink text-[12px] uppercase tracking-[0.24em] font-semibold rounded-sm hover:bg-white transition-all duration-300"
            >
              Contact Us <ArrowRight size={16} />
            </Link>
            <Link
              to="/services"
              data-testid="home-cta-services-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white text-[12px] uppercase tracking-[0.24em] font-semibold rounded-sm hover:bg-white/10 transition-all duration-300"
            >
              Our Solutions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
